import { useCallback, useEffect, useMemo, useRef } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";

const STOP_TYPING_DELAY_MS = 3000;

export function useTypingUsers(channelId: string | undefined) {
	const currentUserId = useStore(authStore, (state) => state.user?.uid);
	const typingUsersMap = useStore(
		coreStore,
		useShallow((state) => state.typingUsers)
	);

	const typingUsers = useMemo(() => {
		if (!channelId) return [];
		const users: string[] = [];
		for (const [, user] of typingUsersMap) {
			if (user.channelId === channelId && user.firebaseUid !== currentUserId) {
				users.push(user.firebaseUid);
			}
		}
		return users;
	}, [channelId, currentUserId, typingUsersMap]);

	return typingUsers;
}

export function useTypingIndicator(channelId: string | undefined) {
	const stopTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null
	);
	const isTypingRef = useRef(false);

	const startTyping = useCallback(() => {
		if (!channelId) return;

		coreStore.getState().startTyping(channelId);
		isTypingRef.current = true;

		if (stopTypingTimeoutRef.current) {
			clearTimeout(stopTypingTimeoutRef.current);
		}

		stopTypingTimeoutRef.current = setTimeout(() => {
			if (channelId && isTypingRef.current) {
				coreStore.getState().stopTyping(channelId);
				isTypingRef.current = false;
			}
		}, STOP_TYPING_DELAY_MS);
	}, [channelId]);

	const stopTyping = useCallback(() => {
		if (!channelId) return;

		if (stopTypingTimeoutRef.current) {
			clearTimeout(stopTypingTimeoutRef.current);
			stopTypingTimeoutRef.current = null;
		}

		if (isTypingRef.current) {
			coreStore.getState().stopTyping(channelId);
			isTypingRef.current = false;
		}
	}, [channelId]);

	useEffect(
		() => () => {
			if (stopTypingTimeoutRef.current) {
				clearTimeout(stopTypingTimeoutRef.current);
			}
			if (channelId && isTypingRef.current) {
				coreStore.getState().stopTyping(channelId);
			}
		},
		[channelId]
	);

	return { startTyping, stopTyping };
}
